import { useState, forwardRef } from 'react';

import MuiTextField from '@mui/material/TextField';

import {
  formatStructuredInput,
  normalizeStructuredInput,
  getStructuredInputAttributes,
} from 'src/utils/structured-input';
import {
  formatLocalizedInput,
  normalizeLocalizedInput,
  shouldLocalizeInputDigits,
  createNormalizedInputEvent,
} from 'src/utils/localized-input';

// ----------------------------------------------------------------------

function getInputMode(type, inputProps, slotProps) {
  const configuredMode = inputProps?.inputMode ?? slotProps?.htmlInput?.inputMode;
  if (configuredMode) return configuredMode;

  if (type !== 'number') return undefined;

  const step = inputProps?.step ?? slotProps?.htmlInput?.step;
  return step === 'any' || (step != null && Number(step) % 1 !== 0) ? 'decimal' : 'numeric';
}

const TextField = forwardRef(
  (
    {
      type,
      value,
      defaultValue,
      onChange,
      onBlur,
      inputProps,
      slotProps,
      select,
      localizeDigits = true,
      inputFormat,
      inputFormatOptions,
      ...other
    },
    ref
  ) => {
    const inputMode = getInputMode(type, inputProps, slotProps);
    const shouldLocalize = shouldLocalizeInputDigits({
      type,
      inputMode,
      name: other.name ?? inputProps?.name,
      autoComplete: other.autoComplete ?? inputProps?.autoComplete,
      select,
      localizeDigits,
    });
    const controlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
    const usesStructuredFormat = !!inputFormat;

    if (!shouldLocalize && !usesStructuredFormat) {
      return (
        <MuiTextField
          ref={ref}
          type={type}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onBlur={onBlur}
          inputProps={inputProps}
          slotProps={slotProps}
          select={select}
          {...other}
        />
      );
    }

    const sourceValue = controlled ? value : uncontrolledValue;
    const structuredOptions = { ...inputFormatOptions, localizeDigits };
    const displayValue = usesStructuredFormat
      ? formatStructuredInput(sourceValue, inputFormat, structuredOptions)
      : formatLocalizedInput(sourceValue, { type, inputMode });
    const structuredAttributes = usesStructuredFormat
      ? getStructuredInputAttributes(inputFormat, structuredOptions)
      : {};

    const handleChange = (event) => {
      const normalized = usesStructuredFormat
        ? normalizeStructuredInput(event.target.value, inputFormat, structuredOptions)
        : normalizeLocalizedInput(event.target.value, { type, inputMode });
      if (!controlled) setUncontrolledValue(normalized);
      onChange?.(createNormalizedInputEvent(event, normalized));
    };

    const handleBlur = (event) => {
      const normalized = usesStructuredFormat
        ? normalizeStructuredInput(event.target.value, inputFormat, structuredOptions)
        : normalizeLocalizedInput(event.target.value, { type, inputMode });
      onBlur?.(createNormalizedInputEvent(event, normalized));
    };

    return (
      <MuiTextField
        ref={ref}
        type={type === 'number' ? 'text' : type}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        inputProps={{
          ...structuredAttributes,
          ...inputProps,
          inputMode: inputProps?.inputMode ?? structuredAttributes.inputMode ?? inputMode,
          maxLength: structuredAttributes.maxLength ?? inputProps?.maxLength,
          lang: 'fa',
          'aria-valuemin': inputProps?.min,
          'aria-valuemax': inputProps?.max,
          'aria-valuenow': type === 'number' && sourceValue !== '' ? sourceValue : undefined,
        }}
        slotProps={slotProps}
        select={select}
        {...other}
      />
    );
  }
);

TextField.displayName = 'LocalizedTextField';

export default TextField;
